/**
 * Theme Context for Dark Mode Support
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

/**
 * Theme Provider Component
 */
export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
    // Check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    
    // Update document class for global dark mode styles
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const value = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme-aware styling utilities
 */
export const getThemeStyles = (isDarkMode) => ({
  // Background colors
  bg: {
    primary: isDarkMode ? 'bg-gray-900' : 'bg-white',
    secondary: isDarkMode ? 'bg-gray-800' : 'bg-gray-50',
    tertiary: isDarkMode ? 'bg-gray-700' : 'bg-gray-100',
    accent: isDarkMode ? 'bg-blue-900' : 'bg-blue-50',
    hover: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
  },
  
  // Text colors
  text: {
    primary: isDarkMode ? 'text-white' : 'text-gray-900',
    secondary: isDarkMode ? 'text-gray-300' : 'text-gray-600',
    muted: isDarkMode ? 'text-gray-400' : 'text-gray-500',
    accent: isDarkMode ? 'text-blue-400' : 'text-blue-600',
  },
  
  // Border colors
  border: {
    primary: isDarkMode ? 'border-gray-700' : 'border-gray-200',
    secondary: isDarkMode ? 'border-gray-600' : 'border-gray-300',
    accent: isDarkMode ? 'border-blue-700' : 'border-blue-200',
  },
  
  // Interactive elements
  button: {
    primary: isDarkMode 
      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
      : 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: isDarkMode 
      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border-gray-600' 
      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300',
    danger: isDarkMode 
      ? 'bg-red-700 hover:bg-red-800 text-white' 
      : 'bg-red-600 hover:bg-red-700 text-white',
  },
  
  // Form elements
  input: {
    primary: isDarkMode 
      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    focus: isDarkMode 
      ? 'focus:ring-blue-500 focus:border-blue-500' 
      : 'focus:ring-blue-500 focus:border-blue-500',
  },
  
  // Cards and containers
  card: {
    primary: isDarkMode 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-200',
    hover: isDarkMode 
      ? 'hover:bg-gray-750 hover:border-gray-600' 
      : 'hover:bg-gray-50 hover:border-gray-300',
  },
  
  // Status indicators
  status: {
    success: isDarkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800',
    warning: isDarkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
    error: isDarkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800',
    info: isDarkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800',
  }
});

export default ThemeContext;
